// Recents for the food picker — how many times each food has been LOGGED.
//
// Pure, no React, no I/O beyond localStorage. Modeled on the recipeViews pair
// (RecipeBrowser.js:3-19 read, RecipeDetail.js:18-24 write) with two deliberate
// departures, both marked below.
//
// This is a convenience index, not a source of truth. The authoritative record
// of what was eaten is Food_Log in SharePoint, which carries FoodId on every
// row; if this key is cleared, wiped by the browser, or absent on a new device,
// the picker loses its ordering and nothing else. Nothing reads it to compute a
// total. That is why a silent catch is acceptable here and would not be in
// foodLog.js.

const KEY = 'foodLogCounts';

// Shape: { [foodId]: integerCount }. Any parse failure yields {} — a corrupt
// value degrades to "no recents", never to a throw inside a render.
export function getLogCounts() {
  try {
    const parsed = JSON.parse(localStorage.getItem(KEY) || '{}');
    // JSON.parse('null') and JSON.parse('[1,2]') both survive the try but are
    // not the shape callers expect. Object.entries would still work on an
    // array, so screen it here rather than at every call site.
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed;
  } catch {
    return {};
  }
}

// Called after a successful createItem, never before. A row that failed to save
// is not something you ate, and inflating the count for it would push a food up
// the picker on the strength of an error.
export function recordLog(foodId) {
  try {
    const counts = getLogCounts();
    counts[foodId] = (counts[foodId] || 0) + 1;
    localStorage.setItem(KEY, JSON.stringify(counts));
  } catch { /* localStorage unavailable or full — recents are optional */ }
}

// DEPARTURE 1 from the recipe pattern: the `> 0` filter.
//
// getTopRecipes sorts the entire catalog with no floor and slices, so on a
// fresh install every id scores 0, sort is stable, and the "top 3" is just the
// first three entries in source order — presented under a heading that claims
// they are favorites. For food that would surface ground beef, ribeye and NY
// strip to someone who has logged none of them. A food with no logs is not a
// recent, so it is excluded outright and the caller renders nothing when the
// array comes back empty.
//
// Ties keep insertion order, which for a JS string-keyed object is the order
// ids were first logged. Good enough: a tie means equal frequency, and there is
// no recency data stored to break it with. If ordering by last-logged is ever
// wanted, that is a shape change here, not at the call site.
export function topFoodIds(counts, n) {
  return Object.entries(counts)
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([foodId]) => foodId);
}

// DEPARTURE 2, which lives at the call site rather than in this file but is
// recorded here because it is the reason these are three separate functions
// instead of one getTopFoods():
//
// RecipeBrowser calls getTopRecipes(3) inline in the component body, so the
// counts are read once when that screen mounts and never again. That is fine
// for a browse screen you navigate into and out of. The add-food sheet stays
// MOUNTED across several logs — the whole point of returning to the picker
// after a save is logging a second item without reopening — so a value captured
// at mount would be stale by the second item. AddFoodSheet therefore holds
// counts in React state and refreshes them in an effect keyed on `open`, and
// again after each successful log, so the recents row reorders as you use it.
